import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Loader2, Building2, Mail, Lock } from 'lucide-react';
import { ClinicLogo } from '@/hooks/useClinicBranding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientLoginPage = ({ setUser }) => {
  const { clinicSubdomain } = useParams();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clinicLoading, setClinicLoading] = useState(true);
  const [clinic, setClinic] = useState(null);
  const [branding, setBranding] = useState(null);
  const [clinicError, setClinicError] = useState(null);

  useEffect(() => {
    fetchClinicInfo();
  }, [clinicSubdomain]);

  const fetchClinicInfo = async () => {
    try {
      setClinicLoading(true);
      
      // Fetch both clinic info and branding
      const [clinicRes, brandingRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/clinic/${clinicSubdomain}`),
        axios.get(`${API_URL}/api/public/clinic/${clinicSubdomain}/branding`).catch(() => null)
      ]);
      
      setClinic(clinicRes.data);
      
      if (brandingRes?.data) {
        setBranding(brandingRes.data);
      }
      
      setClinicError(null);
    } catch (error) {
      console.error('Error fetching clinic:', error);
      setClinicError('Clinic not found. Please check the URL and try again.');
      setClinic(null);
    } finally {
      setClinicLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
        clinic_subdomain: clinicSubdomain
      });

      const { access_token, user } = response.data;
      
      // Verify user is a patient and belongs to this clinic
      if (user.role !== 'patient') {
        toast.error('This login page is for patients only. Please use the appropriate login page.');
        setLoading(false);
        return;
      }

      // Store auth data
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('clinic_id', user.clinic_id);

      setUser(user);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/patient');
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Get colors from branding or use defaults
  const primaryColor = branding?.primary_color || '#14b8a6';
  const secondaryColor = branding?.secondary_color || '#0d9488';

  if (clinicLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: `linear-gradient(to bottom right, ${primaryColor}15, ${secondaryColor}20)` }}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: primaryColor }} />
          <p className="text-gray-600">Loading clinic information...</p>
        </div>
      </div>
    );
  }

  if (clinicError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Clinic Not Found</CardTitle>
            <CardDescription>{clinicError}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              The clinic "{clinicSubdomain}" does not exist or the URL is incorrect.
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const clinicName = branding?.name || clinic?.name || 'Patient Portal';
  const logoUrl = branding?.logo_url || clinic?.logo;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: `linear-gradient(to bottom right, ${primaryColor}15, ${secondaryColor}20)` }}
    >
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          {/* Clinic Logo/Name */}
          <div className="mb-4 flex justify-center">
            <ClinicLogo 
              logoUrl={logoUrl}
              clinicName={clinicName}
              size="lg"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {clinicName}
          </CardTitle>
          <CardDescription className="text-gray-600">
            Sign in to access your patient portal
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <a 
                href="/forgot-password" 
                className="text-sm hover:underline"
                style={{ color: primaryColor }}
              >
                Forgot your password?
              </a>
            </div>

            {/* Login Button - Use clinic primary color */}
            <Button 
              type="submit" 
              className="w-full text-white py-6"
              style={{ 
                background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`,
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Need help? Contact{' '}
              <a 
                href={`mailto:${branding?.email || clinic?.email || 'support@mymedmember.com'}`} 
                className="hover:underline"
                style={{ color: primaryColor }}
              >
                {clinicName}
              </a>
            </p>
          </div>

          {/* Powered By */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              Powered by{' '}
              <a 
                href="https://mymedmember.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:underline"
                style={{ color: primaryColor }}
              >
                MyMedMember by Clinicax Systems
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientLoginPage;
